import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function PartnerCommissions() {
  const [payments, setPayments] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [selectedFile, setSelectedFile] = useState({});

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    const res = await fetch("/api/vendor-payments/", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setPayments(data.data);
    }
  };

  const uploadPaymentProof = async (paymentId, file) => {
    const formData = new FormData();

    formData.append("proof", file);
    formData.append("method", "Bank Transfer");
    formData.append("reference", "TXN123456");
    formData.append("notes", "");

    const res = await fetch(
      `/api/vendor-payments/pay/${paymentId}`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      },
    );

    const data = await res.json();

    if (data.status === "success") {
      alert("Payment marked as paid.");
      loadPayments();
      setUploadingFor(null);
    } else {
      alert(data.message);
    }
  };

  const summary = useMemo(() => {
    const total = payments.reduce((sum, p) => sum + Number(p.amount), 0);

    const pending = payments
      .filter((p) => p.status === "Pending")
      .reduce((sum, p) => sum + Number(p.amount), 0);

    const paid = payments
      .filter((p) => p.status === "Paid")
      .reduce((sum, p) => sum + Number(p.amount), 0);

    return {
      total,
      pending,
      paid,
    };
  }, [payments]);

  return (
    <DashboardLayout>
      <div className="space-y-10">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">Vendor Payments</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard
            label="Total Payables"
            value={`₹${summary.total.toLocaleString()}`}
          />

          <SummaryCard
            label="Pending Payments"
            value={`₹${summary.pending.toLocaleString()}`}
            highlight
          />

          <SummaryCard
            label="Paid Payments"
            value={`₹${summary.paid.toLocaleString()}`}
          />
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Vendor</th>

                <th className="px-6 py-4">Branch</th>

                <th className="px-6 py-4">PO</th>

                <th className="px-6 py-4">Amount</th>

                <th className="px-6 py-4">Due Date</th>

                <th className="px-6 py-4">Status</th>

                <th className="px-6 py-4">Proof</th>

                <th className="px-6 py-4">Action</th>
              </tr>
            </thead>

            <tbody>
              {payments.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-white/10 hover:bg-white/5"
                >
                  <td className="px-6 py-4">{p.vendor}</td>

                  <td className="px-6 py-4 text-center">{p.branch}</td>

                  <td className="px-6 py-4 text-center">{p.po}</td>

                  <td className="px-6 py-4 text-center">
                    ₹{Number(p.amount).toLocaleString()}
                  </td>

                  <td className="px-6 py-4 text-center">
                    {new Date(p.due_date).toLocaleDateString()}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        p.status === "Paid"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-amber-400/20 text-amber-400"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-center"><TableActions>
                    {p.proof ? (
                      <a
                        href={`/api${p.proof}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-amber-400 underline text-xs"
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableActions></td>

                  <td className="px-6 py-4 text-center">
                    {p.status === "Pending" ? (
                      uploadingFor === p.id ? (
                        <div>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) =>
                              setSelectedFile({
                                ...selectedFile,
                                [p.id]: e.target.files[0],
                              })
                            }
                          />
                          <PermissionButton permissionModule="vendor_payments"
                            onClick={() =>
                              uploadPaymentProof(p.id, selectedFile[p.id])
                            }
                            className="bg-green-600 px-3 py-1 rounded text-white text-xs"
                          >
                            Upload
                          </PermissionButton>
                        </div>
                      ) : (
                        <PermissionButton permissionModule="vendor_payments"
                          onClick={() => setUploadingFor(p.id)}
                          className="px-4 py-1 rounded-lg bg-amber-400 text-black text-xs"
                        >
                          Pay Vendor
                        </PermissionButton>
                      )
                    ) : (
                      <span className="text-amber-400">Paid</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs text-gray-400 tracking-widest uppercase">{label}</p>
      <p
        className={`mt-3 text-2xl font-serif ${
          highlight ? "text-amber-400" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
